import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  getCountFromServer,
  writeBatch,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { auth } from '../config/firebase';
import { sanitizeForFirestore } from '../utils/firestoreHelpers';
import {
  Producto,
  CategoriaProducto,
  FiltrosProductos,
  PaginacionProductos,
  ResultadoPaginado,
  UnidadMedida,
  TipoMovimiento
} from '../types';
import { InventarioService } from './inventarioService';
import { EnvasesService } from './envasesService';

export class ProductosService {

  /**
   * Obtener la ruta del tenant del usuario actual
   */
  private static getTenantPath(collectionName: string): string {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('Usuario no autenticado');
    }
    return `tenants/${user.email}/${collectionName}`;
  }

  /**
   * Obtener la ruta del tenant para movimientos de inventario
   */
  private static getMovimientosPath(): string {
    return this.getTenantPath('movimientosInventario');
  }

  /**
   * Obtener la ruta del tenant para categorías de productos
   */
  private static getCategoriasPath(): string {
    return this.getTenantPath('categoriasProductos');
  }

  /**
   * Obtener todos los productos con filtros opcionales
   */
  static async getProductos(filtros?: FiltrosProductos): Promise<Producto[]> {
    try {
      let q: ReturnType<typeof collection> | ReturnType<typeof query> = collection(db, this.getTenantPath('productos'));
      const constraints = [];

      // Aplicar filtros
      if (filtros?.categoria) {
        constraints.push(where('categoria', '==', filtros.categoria));
      }
      
      if (filtros?.activo !== undefined) {
        constraints.push(where('activo', '==', filtros.activo));
      }
      
      if (filtros?.proveedor) {
        constraints.push(where('proveedor', '==', filtros.proveedor));
      }

      // Ordenar por nombre
      constraints.push(orderBy('nombre', 'asc'));

      if (constraints.length > 0) {
        q = query(collection(db, this.getTenantPath('productos')), ...constraints);
      }

      const querySnapshot = await getDocs(q);
      const productos = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const convertedData = this.convertTimestamps(data as DocumentData) as Omit<Producto, 'id'>;
        return {
          id: doc.id,
          ...convertedData
        };
      });

      // Filtros que no se pueden hacer en Firestore
      let productosFiltrados = productos;

      if (filtros?.busqueda) {
        const busqueda = filtros.busqueda.toLowerCase();
        productosFiltrados = productos.filter(p => 
          p.nombre.toLowerCase().includes(busqueda) ||
          p.descripcion?.toLowerCase().includes(busqueda) ||
          p.codigo?.toLowerCase().includes(busqueda) ||
          p.codigoBarras?.toLowerCase().includes(busqueda)
        );
      }

      if (filtros?.conStock) {
        productosFiltrados = productosFiltrados.filter(p => p.stock > 0);
      }

      if (filtros?.stockBajo) {
        productosFiltrados = productosFiltrados.filter(p => p.stock <= p.stockMinimo);
      }

      // Filtro por tipo de envase
      if (filtros?.envaseRetornable !== undefined && filtros?.envaseRetornable !== 'todos') {
        productosFiltrados = productosFiltrados.filter(p => p.envaseRetornable === filtros.envaseRetornable);
      }

      return productosFiltrados;
    } catch (error) {
      console.error('Error al obtener productos:', error);
      throw error;
    }
  }

  /**
   * Obtener productos con paginación y filtros del lado del servidor
   */
  static async getProductosPaginados(
    filtros: FiltrosProductos = {},
    paginacion: PaginacionProductos = { pagina: 1, limite: 20 }
  ): Promise<ResultadoPaginado<Producto>> {
    try {
      const { pagina, limite, ordenarPor = 'nombre', orden = 'asc' } = paginacion;
      const constraints: QueryConstraint[] = [];

      // Aplicar filtros del lado del servidor
      if (filtros.categoria) {
        constraints.push(where('categoria', '==', filtros.categoria));
      }
      
      if (filtros.activo !== undefined) {
        constraints.push(where('activo', '==', filtros.activo));
      }
      
      if (filtros.proveedor) {
        constraints.push(where('proveedor', '==', filtros.proveedor));
      }

      if (filtros.conStock) {
        constraints.push(where('stock', '>', 0));
      }

      if (filtros.stockBajo) {
        // Para stock bajo, necesitamos hacer una consulta más compleja
        // Por ahora, lo manejaremos en el cliente después de la paginación
      }

      // Ordenar
      constraints.push(orderBy(ordenarPor, orden));

      // Obtener el total de documentos para calcular paginación
      const countQuery = query(collection(db, this.getTenantPath('productos')), ...constraints);
      const countSnapshot = await getCountFromServer(countQuery);
      const totalElementos = countSnapshot.data().count;

      // Calcular paginación
      const totalPaginas = Math.ceil(totalElementos / limite);

      // Aplicar límite
      constraints.push(limit(limite));

      // Crear query final
      const q = query(collection(db, this.getTenantPath('productos')), ...constraints);
      const querySnapshot = await getDocs(q);
      
      const productos = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const convertedData = this.convertTimestamps(data as DocumentData) as Omit<Producto, 'id'>;
        return {
          id: doc.id,
          ...convertedData
        };
      });

      // Aplicar filtros que no se pueden hacer en Firestore
      let productosFiltrados = productos;

      // Filtro de búsqueda (se mantiene en cliente por limitaciones de Firestore)
      if (filtros.busqueda) {
        const busqueda = filtros.busqueda.toLowerCase();
        productosFiltrados = productos.filter(p => 
          p.nombre.toLowerCase().includes(busqueda) ||
          p.descripcion?.toLowerCase().includes(busqueda) ||
          p.codigo?.toLowerCase().includes(busqueda) ||
          p.codigoBarras?.toLowerCase().includes(busqueda)
        );
      }

      // Filtro de stock bajo (se mantiene en cliente)
      if (filtros.stockBajo) {
        productosFiltrados = productosFiltrados.filter(p => p.stock <= p.stockMinimo);
      }

      return {
        datos: productosFiltrados,
        paginacion: {
          paginaActual: pagina,
          totalPaginas,
          totalElementos,
          limite,
          tieneSiguiente: pagina < totalPaginas,
          tieneAnterior: pagina > 1
        }
      };
    } catch (error) {
      console.error('Error al obtener productos paginados:', error);
      throw error;
    }
  }

  /**
   * Obtener un producto por ID
   */
  static async getProducto(id: string): Promise<Producto | null> {
    try {
      const docRef = doc(db, this.getTenantPath('productos'), id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const convertedData = this.convertTimestamps(data as DocumentData) as Omit<Producto, 'id'>;
        return {
          id: docSnap.id,
          ...convertedData
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error al obtener producto:', error);
      throw error;
    }
  }

  /**
   * Crear un nuevo producto
   */
  static async crearProducto(producto: Omit<Producto, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Validar que no exista otro producto con el mismo código
      if (producto.codigo) {
        const existente = await this.getProductoPorCodigo(producto.codigo);
        if (existente) {
          throw new Error('Ya existe un producto con ese código');
        }
      }

      // NUEVO: Asignar envaseTipoId automáticamente si es retornable
      const productoConEnvase = await this.asignarEnvaseTipoId(producto);

      // Sanitizar datos para Firestore
      const productoData = sanitizeForFirestore({
        ...productoConEnvase,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const docRef = await addDoc(collection(db, this.getTenantPath('productos')), productoData as Record<string, unknown>);


      // Registrar movimiento inicial si el producto tiene stock
      if (producto.stock && producto.stock > 0) {
        try {
          await InventarioService.registrarMovimiento(
            docRef.id,
            TipoMovimiento.INICIAL,
            producto.stock,
            'Stock inicial del producto',
            auth.currentUser?.email || 'sistema',
            'creacion_producto',
            `Producto creado con stock inicial: ${producto.stock} unidades`
          );
        } catch (error) {
          console.error('Error al registrar movimiento inicial:', error);
          // No lanzar error para no interrumpir la creación del producto
        }
      }

      return docRef.id;
    } catch (error) {
      console.error('Error al crear producto:', error);
      throw error;
    }
  }

  /**
   * Actualizar un producto
   */
  static async actualizarProducto(id: string, producto: Partial<Producto>): Promise<void> {
    try {
      // Validar código único si se está actualizando
      if (producto.codigo) {
        const existente = await this.getProductoPorCodigo(producto.codigo);
        if (existente && existente.id !== id) {
          throw new Error('Ya existe un producto con ese código');
        }
      }

      // NUEVO: Asignar envaseTipoId automáticamente si es retornable
      const productoConEnvase = await this.asignarEnvaseTipoId(producto);

      const docRef = doc(db, this.getTenantPath('productos'), id);
      
      // Sanitizar datos para Firestore
      const updateData = sanitizeForFirestore({
        ...productoConEnvase,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(docRef, updateData as Record<string, unknown>);
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      throw error;
    }
  }

  /**
   * Eliminar un producto (soft delete)
   */
  static async eliminarProducto(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.getTenantPath('productos'), id);
      await updateDoc(docRef, {
        activo: false,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      throw error;
    }
  }

  /**
   * Eliminar un producto permanentemente (hard delete)
   * ⚠️ ADVERTENCIA: Esta acción no se puede deshacer
   */
  static async eliminarProductoPermanentemente(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.getTenantPath('productos'), id);
      await deleteDoc(docRef);
      console.log(`✅ Producto ${id} eliminado permanentemente`);
    } catch (error) {
      console.error('❌ Error eliminando producto permanentemente:', error);
      throw error;
    }
  }

  /**
   * Obtener producto por código
   */
  static async getProductoPorCodigo(codigo: string): Promise<Producto | null> {
    try {
      const q = query(
        collection(db, this.getTenantPath('productos')),
        where('codigo', '==', codigo),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const convertedData = this.convertTimestamps(data as DocumentData) as Omit<Producto, 'id'>;
        return {
          id: doc.id,
          ...convertedData
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error al buscar producto por código:', error);
      throw error;
    }
  }

  /**
   * Actualizar stock de un producto
   */
  static async actualizarStock(
    productoId: string, 
    nuevaCantidad: number, 
    motivo: string,
    usuario: string,
    referencia?: string
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Obtener producto actual
      const producto = await this.getProducto(productoId);
      if (!producto) {
        throw new Error('Producto no encontrado');
      }

      // Actualizar stock del producto
      const productoRef = doc(db, this.getTenantPath('productos'), productoId);
      batch.update(productoRef, {
        stock: nuevaCantidad,
        updatedAt: serverTimestamp()
      });

      // Crear movimiento de inventario
      const movimientoRef = doc(collection(db, this.getMovimientosPath()));
      batch.set(movimientoRef, {
        productoId,
        tipo: nuevaCantidad > producto.stock ? TipoMovimiento.ENTRADA : TipoMovimiento.SALIDA,
        cantidad: Math.abs(nuevaCantidad - producto.stock),
        cantidadAnterior: producto.stock,
        cantidadNueva: nuevaCantidad,
        motivo,
        referencia,
        fecha: serverTimestamp(),
        usuario,
        createdAt: serverTimestamp()
      });

      await batch.commit();
    } catch (error) {
      console.error('Error al actualizar stock:', error);
      throw error;
    }
  }

  /**
   * Generar código automático para producto
   */
  static async generarCodigoAutomatico(categoria: string): Promise<string> {
    try {
      // Obtener productos de la misma categoría (sin orderBy para evitar índice compuesto)
      const q = query(
        collection(db, this.getTenantPath('productos')),
        where('categoria', '==', categoria)
      );
      
      const querySnapshot = await getDocs(q);
      let numeroSiguiente = 1;
      
      if (!querySnapshot.empty) {
        // Buscar el código más alto en el cliente (más eficiente)
        let maxNumero = 0;
        const prefijo = categoria.substring(0, 3).toUpperCase();
        
        querySnapshot.docs.forEach(doc => {
          const producto = doc.data();
          if (producto.codigo && producto.codigo.startsWith(prefijo)) {
            const match = producto.codigo.match(/(\d+)$/);
            if (match) {
              const numero = parseInt(match[1]);
              if (numero > maxNumero) {
                maxNumero = numero;
              }
            }
          }
        });
        
        numeroSiguiente = maxNumero + 1;
      }

      // Generar código con formato CAT-001
      const prefijo = categoria.substring(0, 3).toUpperCase();
      return `${prefijo}-${numeroSiguiente.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error al generar código automático:', error);
      throw error;
    }
  }

  // ============================================
  // GESTIÓN DE CATEGORÍAS
  // ============================================

  /**
   * Obtener todas las categorías
   */
  static async getCategorias(): Promise<CategoriaProducto[]> {
    try {
      const q = query(
        collection(db, this.getCategoriasPath()),
        orderBy('orden', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        const convertedData = this.convertTimestamps(data as DocumentData) as Omit<CategoriaProducto, 'id'>;
        return {
          id: doc.id,
          ...convertedData
        };
      });
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      throw error;
    }
  }

  /**
   * Crear nueva categoría (con verificación de duplicados)
   */
  static async crearCategoria(categoria: Omit<CategoriaProducto, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Verificar si ya existe una categoría con el mismo nombre
      const categoriasExistentes = await this.getCategorias();
      const categoriaExistente = categoriasExistentes.find(cat => 
        cat.nombre.toLowerCase() === categoria.nombre.toLowerCase()
      );

      if (categoriaExistente) {
        return categoriaExistente.id;
      }

      const docRef = await addDoc(collection(db, this.getCategoriasPath()), {
        ...categoria,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error al crear categoría:', error);
      throw error;
    }
  }

  /**
   * Actualizar categoría
   */
  static async actualizarCategoria(id: string, categoria: Partial<CategoriaProducto>): Promise<void> {
    try {
      const docRef = doc(db, this.getCategoriasPath(), id);
      await updateDoc(docRef, {
        ...categoria,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error al actualizar categoría:', error);
      throw error;
    }
  }

  /**
   * Eliminar categoría
   */
  static async eliminarCategoria(id: string): Promise<void> {
    try {
      // Verificar que no haya productos usando esta categoría
      const productosConCategoria = await this.getProductos({ categoria: id });
      if (productosConCategoria.length > 0) {
        throw new Error('No se puede eliminar la categoría porque tiene productos asociados');
      }

      const docRef = doc(db, this.getCategoriasPath(), id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      throw error;
    }
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Obtener productos con stock bajo
   */
  static async getProductosStockBajo(): Promise<Producto[]> {
    try {
      // CAMBIO: Remover filtro de activo para incluir todos los productos
      const productos = await this.getProductos(); // Sin { activo: true }
      return productos.filter(p => p.stock <= p.stockMinimo);
    } catch (error) {
      console.error('Error al obtener productos con stock bajo:', error);
      throw error;
    }
  }

  /**
   * Obtener productos agotados
   */
  static async getProductosAgotados(): Promise<Producto[]> {
    try {
      const productos = await this.getProductos({ activo: true });
      return productos.filter(p => p.stock === 0);
    } catch (error) {
      console.error('Error al obtener productos agotados:', error);
      throw error;
    }
  }

  /**
   * Obtener valor total del inventario
   */
  static async getValorTotalInventario(): Promise<number> {
    try {
      const productos = await this.getProductos({ activo: true });
      return productos.reduce((total, producto) => {
        return total + (producto.stock * producto.precioCompra);
      }, 0);
    } catch (error) {
      console.error('Error al calcular valor total del inventario:', error);
      throw error;
    }
  }

  /**
   * Asignar envaseTipoId automáticamente basándose en el nombre y capacidad del producto
   */
  private static async asignarEnvaseTipoId<T extends Partial<Producto>>(producto: T): Promise<T> {
    // Solo procesar si el producto es retornable
    if (!producto.envaseRetornable) {
      return producto;
    }

    try {
      // Obtener tipos de envases disponibles
      const tiposEnvases = await EnvasesService.getTiposEnvases();
      
      // Mapeo inteligente basado en nombre y capacidad
      let envaseTipoId: string | undefined;
      const nombre = producto.nombre?.toLowerCase() || '';
      const capacidad = producto.envaseCapacidadLitros;

      // Buscar por capacidad específica primero
      if (capacidad) {
        if (capacidad >= 19 && capacidad <= 21) {
          // Bidón 20L
          const tipo20L = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('20') && 
            t.nombre.toLowerCase().includes('bidón')
          );
          envaseTipoId = tipo20L?.id;
        } else if (capacidad >= 9 && capacidad <= 11) {
          // Bidón 10L
          const tipo10L = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('10') && 
            t.nombre.toLowerCase().includes('bidón')
          );
          envaseTipoId = tipo10L?.id;
        } else if (capacidad >= 0.4 && capacidad <= 0.6) {
          // Soda 500ml (aunque normalmente es descartable)
          const tipoSoda = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('soda') || 
            t.nombre.toLowerCase().includes('500')
          );
          envaseTipoId = tipoSoda?.id;
        }
      }

      // Si no se encontró por capacidad, buscar por nombre (más flexible)
      if (!envaseTipoId) {
        // Bidón 20L - patrones más amplios
        if (nombre.includes('20') || 
            (nombre.includes('bidón') && nombre.includes('20')) ||
            (nombre.includes('bidon') && nombre.includes('20'))) {
          const tipo20L = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('20') && 
            (t.nombre.toLowerCase().includes('bidón') || t.nombre.toLowerCase().includes('bidon'))
          );
          envaseTipoId = tipo20L?.id;
        } 
        // Bidón 10L - patrones más amplios
        else if (nombre.includes('10') || 
                 (nombre.includes('bidón') && nombre.includes('10')) ||
                 (nombre.includes('bidon') && nombre.includes('10'))) {
          const tipo10L = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('10') && 
            (t.nombre.toLowerCase().includes('bidón') || t.nombre.toLowerCase().includes('bidon'))
          );
          envaseTipoId = tipo10L?.id;
        }
        // Bidón 12L - nuevo patrón
        else if (nombre.includes('12') || 
                 (nombre.includes('bidón') && nombre.includes('12')) ||
                 (nombre.includes('bidon') && nombre.includes('12'))) {
          // Buscar bidón 10L como fallback para 12L (son similares)
          const tipo12L = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('10') && 
            (t.nombre.toLowerCase().includes('bidón') || t.nombre.toLowerCase().includes('bidon'))
          );
          envaseTipoId = tipo12L?.id;
        }
        // Agua genérica - mapear a bidón 20L por defecto
        else if (nombre.includes('agua') && !nombre.includes('soda')) {
          const tipoAgua = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('20') && 
            (t.nombre.toLowerCase().includes('bidón') || t.nombre.toLowerCase().includes('bidon'))
          );
          envaseTipoId = tipoAgua?.id;
        }
        // Soda - patrones existentes
        else if (nombre.includes('soda') || nombre.includes('500')) {
          const tipoSoda = tiposEnvases.find((t: { id: string; nombre: string }) => 
            t.nombre.toLowerCase().includes('soda') || 
            t.nombre.toLowerCase().includes('500')
          );
          envaseTipoId = tipoSoda?.id;
        }
      }

      // Si se encontró un tipo de envase, agregarlo al producto
      if (envaseTipoId) {
        const tipoEnvase = tiposEnvases.find((t: { id: string; nombre: string }) => t.id === envaseTipoId);
        return {
          ...producto,
          envaseTipoId,
          envaseNombreCompleto: tipoEnvase?.nombre || producto.envaseNombreCompleto
        };
      }

      return producto;
    } catch (error) {
      console.error('Error asignando envaseTipoId:', error);
      return producto; // Devolver producto sin cambios si hay error
    }
  }

  /**
   * Actualizar productos retornables existentes que no tienen envaseTipoId
   */
  static async actualizarProductosRetornablesSinTipoId(): Promise<void> {
    try {
      // Obtener productos retornables sin envaseTipoId
      const productos = await this.getProductos({ activo: true });
      const productosRetornablesSinTipo = productos.filter(p => 
        p.envaseRetornable === true && !p.envaseTipoId
      );
      
      if (productosRetornablesSinTipo.length === 0) {
        return;
      }
      
      const batch = writeBatch(db);
      let actualizados = 0;
      
      for (const producto of productosRetornablesSinTipo) {
        try {
          // Usar la función de asignación automática
          const productoConEnvase = await this.asignarEnvaseTipoId(producto);
          
          // Solo actualizar si se asignó un envaseTipoId
          if (productoConEnvase.envaseTipoId && productoConEnvase.envaseTipoId !== producto.envaseTipoId) {
            const docRef = doc(db, this.getTenantPath('productos'), producto.id);
            batch.update(docRef, {
              envaseTipoId: productoConEnvase.envaseTipoId,
              envaseNombreCompleto: productoConEnvase.envaseNombreCompleto,
              updatedAt: serverTimestamp()
            });
            actualizados++;
          }
        } catch (error) {
          console.error(`Error procesando ${producto.nombre}:`, error);
        }
      }
      
      if (actualizados > 0) {
        await batch.commit();
      }
    } catch (error) {
      console.error('❌ Error actualizando productos retornables:', error);
      throw error;
    }
  }

  /**
   * Actualizar productos existentes con información de envases (legacy)
   */
  static async actualizarProductosConEnvases(): Promise<void> {
    try {
      
      // Mapeo de productos a tipos de envases
      const mapeoEnvases: Record<string, {
        envaseTipoId: string;
        envaseTipo: 'comodato' | 'retornable' | 'descartable';
        envaseCapacidad: number;
        envaseNombre: string;
      }> = {
        'Soda 500ml': {
          envaseTipoId: 'Fu6lba5GQEB13XzHCCCx',
          envaseTipo: 'descartable',
          envaseCapacidad: 0.5,
          envaseNombre: 'Botella Soda 500ml'
        },
        'Bidón 10L': {
          envaseTipoId: '51ny021gOfqqWiWMxEel',
          envaseTipo: 'retornable',
          envaseCapacidad: 10,
          envaseNombre: 'Bidón 10L'
        },
        'Bidón 20L': {
          envaseTipoId: 'Ft8RvqfE7z9ALxEvXGTr',
          envaseTipo: 'retornable',
          envaseCapacidad: 20,
          envaseNombre: 'Bidón 20L'
        }
      };

      const productos = await this.getProductos({ activo: true });
      const batch = writeBatch(db);
      let actualizados = 0;

      productos.forEach(producto => {
        const mapeo = mapeoEnvases[producto.nombre];
        if (mapeo && !producto.envaseTipoId) {
          const docRef = doc(db, this.getTenantPath('productos'), producto.id);
          batch.update(docRef, {
            envaseTipoId: mapeo.envaseTipoId,
            envaseTipo: mapeo.envaseTipo,
            envaseCapacidad: mapeo.envaseCapacidad,
            envaseNombre: mapeo.envaseNombre,
            updatedAt: serverTimestamp()
          });
          actualizados++;
        }
      });

      if (actualizados > 0) {
        await batch.commit();
      }
    } catch (error) {
      console.error('Error al actualizar productos con envases:', error);
      throw error;
    }
  }

  /**
   * Validar disponibilidad de stock para una venta
   */
  static async validarDisponibilidad(productos: { productoId: string; cantidad: number }[]): Promise<{
    disponible: boolean;
    faltantes: { productoId: string; nombre: string; disponible: number; solicitado: number }[];
  }> {
    try {
      const faltantes = [];
      
      for (const item of productos) {
        const producto = await this.getProducto(item.productoId);
        if (!producto) {
          faltantes.push({
            productoId: item.productoId,
            nombre: 'Producto no encontrado',
            disponible: 0,
            solicitado: item.cantidad
          });
        } else if (producto.stock < item.cantidad) {
          faltantes.push({
            productoId: item.productoId,
            nombre: producto.nombre,
            disponible: producto.stock,
            solicitado: item.cantidad
          });
        }
      }

      return {
        disponible: faltantes.length === 0,
        faltantes
      };
    } catch (error) {
      console.error('Error al validar disponibilidad:', error);
      throw error;
    }
  }

  /**
   * Convertir timestamps de Firestore a Date
   */
  private static convertTimestamps<T = unknown>(data: T): T extends Timestamp
    ? Date
    : T extends (infer U)[]
      ? ReturnType<typeof this.convertTimestamps<U>>[]
      : T extends Record<string, unknown>
        ? { [K in keyof T]: ReturnType<typeof this.convertTimestamps<T[K]>> }
        : T {
    if (data instanceof Timestamp) {
      return data.toDate() as ReturnType<typeof this.convertTimestamps<T>>;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.convertTimestamps(item)) as ReturnType<typeof this.convertTimestamps<T>>;
    }

    if (data && typeof data === 'object') {
      const converted: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        converted[key] = this.convertTimestamps(value);
      }
      return converted as ReturnType<typeof this.convertTimestamps<T>>;
    }

    return data as ReturnType<typeof this.convertTimestamps<T>>;
  }

  /**
   * Crear productos iniciales por defecto
   */
  static async crearProductosIniciales(usuario: string): Promise<void> {
    try {
      // Crear categorías específicas para sodero
      const categoriaAgua = await this.crearCategoria({
        nombre: 'Agua',
        descripcion: 'Productos de agua purificada',
        color: '#3B82F6',
        icono: 'droplets',
        activa: true,
        orden: 1
      });

      const categoriaSodas = await this.crearCategoria({
        nombre: 'Sodas',
        descripcion: 'Bebidas gaseosas y sodas',
        color: '#EF4444',
        icono: 'cup-soda',
        activa: true,
        orden: 2
      });

      const categoriaAguaSaborizada = await this.crearCategoria({
        nombre: 'Agua Saborizada',
        descripcion: 'Agua con sabores naturales',
        color: '#10B981',
        icono: 'droplets',
        activa: true,
        orden: 3
      });

      const categoriaJugos = await this.crearCategoria({
        nombre: 'Jugos',
        descripcion: 'Jugos naturales y procesados',
        color: '#F97316',
        icono: 'coffee',
        activa: true,
        orden: 4
      });

      const categoriaEnergizantes = await this.crearCategoria({
        nombre: 'Energizantes',
        descripcion: 'Bebidas energéticas y deportivas',
        color: '#F59E0B',
        icono: 'zap',
        activa: true,
        orden: 5
      });

      // Categoría de Cervezas creada pero sin productos iniciales por ahora
      // const categoriaCervezas = await this.crearCategoria({
      //   nombre: 'Cervezas',
      //   descripcion: 'Cervezas y bebidas alcohólicas',
      //   color: '#8B5CF6',
      //   icono: 'beer',
      //   activa: true,
      //   orden: 6
      // });


      // Crear productos por defecto para sodero
      const productosIniciales = [
        // Productos de Agua
        {
          nombre: 'Bidón 10L',
          descripcion: 'Bidón de agua purificada 10 litros',
          categoria: categoriaAgua,
          codigo: 'AGU-001',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 200,
          precioVenta: 300,
          stock: 50,
          stockMinimo: 10,
          volumen: 10000,
          activo: true,
          envaseTipoId: '51ny021gOfqqWiWMxEel',
          envaseTipo: 'retornable' as const,
          envaseCapacidad: 10,
          envaseNombre: 'Bidón 10L',
          createdBy: usuario,
          updatedBy: usuario
        },
        {
          nombre: 'Bidón 20L',
          descripcion: 'Bidón de agua purificada 20 litros',
          categoria: categoriaAgua,
          codigo: 'AGU-002',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 350,
          precioVenta: 500,
          stock: 30,
          stockMinimo: 5,
          volumen: 20000,
          activo: true,
          envaseTipoId: 'Ft8RvqfE7z9ALxEvXGTr',
          envaseTipo: 'retornable' as const,
          envaseCapacidad: 20,
          envaseNombre: 'Bidón 20L',
          createdBy: usuario,
          updatedBy: usuario
        },

        // Productos de Sodas
        {
          nombre: 'Soda 500ml',
          descripcion: 'Soda saborizada 500ml',
          categoria: categoriaSodas,
          codigo: 'SOD-001',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 30,
          precioVenta: 50,
          stock: 100,
          stockMinimo: 20,
          activo: true,
          envaseTipoId: 'Fu6lba5GQEB13XzHCCCx',
          envaseTipo: 'descartable' as const,
          envaseCapacidad: 0.5,
          envaseNombre: 'Botella Soda 500ml',
          createdBy: usuario,
          updatedBy: usuario
        },

        // Productos de Agua Saborizada
        {
          nombre: 'Agua Saborizada 500ml',
          descripcion: 'Agua saborizada natural 500ml',
          categoria: categoriaAguaSaborizada,
          codigo: 'AGS-001',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 35,
          precioVenta: 55,
          stock: 80,
          stockMinimo: 15,
          activo: true,
          envaseTipoId: 'Fu6lba5GQEB13XzHCCCx',
          envaseTipo: 'descartable' as const,
          envaseCapacidad: 0.5,
          envaseNombre: 'Botella Agua Saborizada 500ml',
          createdBy: usuario,
          updatedBy: usuario
        },

        // Productos de Jugos
        {
          nombre: 'Jugo Naranja 1L',
          descripcion: 'Jugo de naranja natural 1 litro',
          categoria: categoriaJugos,
          codigo: 'JUG-001',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 45,
          precioVenta: 70,
          stock: 60,
          stockMinimo: 12,
          activo: true,
          envaseTipoId: 'Fu6lba5GQEB13XzHCCCx',
          envaseTipo: 'descartable' as const,
          envaseCapacidad: 1,
          envaseNombre: 'Botella Jugo 1L',
          createdBy: usuario,
          updatedBy: usuario
        },

        // Productos Energizantes
        {
          nombre: 'Energizante 250ml',
          descripcion: 'Bebida energética 250ml',
          categoria: categoriaEnergizantes,
          codigo: 'ENE-001',
          unidadMedida: UnidadMedida.UNIDAD,
          precioCompra: 60,
          precioVenta: 90,
          stock: 40,
          stockMinimo: 8,
          activo: true,
          envaseTipoId: 'Fu6lba5GQEB13XzHCCCx',
          envaseTipo: 'descartable' as const,
          envaseCapacidad: 0.25,
          envaseNombre: 'Lata Energizante 250ml',
          createdBy: usuario,
          updatedBy: usuario
        }
      ];

      // Crear productos usando batch
      const batch = writeBatch(db);
      productosIniciales.forEach(producto => {
        const docRef = doc(collection(db, this.getTenantPath('productos')));
        batch.set(docRef, {
          ...producto,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error al crear productos iniciales:', error);
      throw error;
    }
  }

  /**
   * Obtener tipos de envases de comodato desde productos retornables del inventario
   * Esta función hace que el sistema sea dinámico y conectado al inventario real
   */
  static async getTiposEnvasesDesdeProductos(): Promise<Array<{id: string, nombre: string, tipo: 'comodato', capacidad_litros: number, activo: boolean, createdAt: Date, updatedAt: Date}>> {
    try {
      // 1. Obtener productos activos que son retornables
      const productos = await this.getProductos({ 
        activo: true, 
        envaseRetornable: true 
      });

      if (productos.length === 0) {
        console.warn('⚠️ No hay productos retornables en el inventario');
        return [];
      }

      // 2. Extraer tipos de envases únicos de los productos
      const tiposEnvasesMap = new Map<string, {id: string, nombre: string, tipo: 'comodato', capacidad_litros: number, activo: boolean, createdAt: Date, updatedAt: Date}>();
      
      productos.forEach(producto => {
        if (producto.envaseTipoId && producto.envaseNombreCompleto) {
          tiposEnvasesMap.set(producto.envaseTipoId, {
            id: producto.envaseTipoId,
            nombre: producto.envaseNombreCompleto,
            tipo: 'comodato',
            capacidad_litros: producto.envaseCapacidadLitros || 0,
            activo: true,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
      });

      // 3. Convertir Map a Array y ordenar por nombre
      const tiposEnvases = Array.from(tiposEnvasesMap.values())
        .sort((a, b) => a.nombre.localeCompare(b.nombre));

      return tiposEnvases;

    } catch (error) {
      console.error('❌ Error obteniendo tipos de envases desde productos:', error);
      return [];
    }
  }

}
